import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../api/axios';
import { CheckCircleIcon, TrashIcon, UserGroupIcon, PencilIcon, ArrowUpTrayIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import AssignSevakModal from '../../../components/AssignSevakModal'; // Reusable modal
import UploadCSVModal from '../../../components/UploadCSVModal'; // Reusable modal

const ProfileLeads = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAssignModalOpen, setAssignModalOpen] = useState(false);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false); // State for upload modal
    const [selectedLead, setSelectedLead] = useState(null);

    const fetchLeads = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/crm/leads/profiles');
            setLeads(res.data);
        } catch (error) {
            console.error("Failed to fetch profile leads:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);

    const handleConvert = async (id) => {
        if (window.confirm("Convert this lead to a customer?")) {
            await api.put(`/crm/profile/${id}/convert_to_customer`);
            fetchLeads();
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this profile lead?")) {
            await api.delete(`/crm/profile/delete/${id}`);
            fetchLeads();
        }
    };
    
    const openAssignModal = (lead) => {
        setSelectedLead(lead);
        setAssignModalOpen(true);
    };

    if (loading) return <div className="p-4">Loading profile leads...</div>;

    return (
        <>
            <div className="flex justify-end items-center gap-4 mb-4">
                <Link to="/profile/new" className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                    <UserPlusIcon className="h-5 w-5 mr-2" />
                    Add Profile Lead
                </Link>
                <button onClick={() => setUploadModalOpen(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700">
                    <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                    Upload CSV
                </button>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                     <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile Lead</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Sevak</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Added</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {leads.length > 0 ? leads.map((lead) => (
                            <tr key={lead.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                                    <div className="text-sm text-gray-500">{lead.slug}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{lead.assigned_admin_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(lead.created_at).toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium space-x-4">
                                    <button onClick={() => handleConvert(lead.id)} title="Approve & Convert"><CheckCircleIcon className="h-5 w-5 text-green-600 hover:text-green-900" /></button>
                                    <button onClick={() => openAssignModal(lead)} title="Assign Sevak"><UserGroupIcon className="h-5 w-5 text-blue-600 hover:text-blue-900" /></button>
                                    <Link to={`/profile/edit/${lead.id}`} title="Edit Lead"><PencilIcon className="h-5 w-5 text-indigo-600 hover:text-indigo-900" /></Link>
                                    <button onClick={() => handleDelete(lead.id)} title="Delete Lead"><TrashIcon className="h-5 w-5 text-red-600 hover:text-red-900" /></button>
                                </td>
                            </tr>
                        )) : <tr><td colSpan="4" className="text-center py-4 text-gray-500">No profile leads found.</td></tr>}
                    </tbody>
                </table>
            </div>
            
            {/* We need to make these modals generic to handle both lead types */}
            <AssignSevakModal isOpen={isAssignModalOpen} onClose={() => setAssignModalOpen(false)} lead={selectedLead} leadType="profile" onAssignSuccess={fetchLeads} />
            <UploadCSVModal isOpen={isUploadModalOpen} onClose={() => setUploadModalOpen(false)} uploadUrl="/crm/profile/upload_csv" requiredColumns="name, slug" onUploadSuccess={fetchLeads} />
        </>
    );
};

export default ProfileLeads;